import { Dispatch, SetStateAction, createContext, useContext } from 'react';
import { API } from '@shared/types/api';
import { createCtx } from '@shared/util/context';

export interface ViewTemplateState {
  viewTemplate: API.ViewTemplateFragment;
  savedViewTemplate: API.ViewTemplateFragment;
  isModified?: boolean;
}

export interface ViewTemplateActions {
  setViewTemplate: Dispatch<SetStateAction<API.ViewTemplateFragment>>;
  revertViewTemplate: VoidFunction;
  saveViewTemplate: () => Promise<void>;
}

export const [useViewTemplateState, ViewTemplateStateProvider] = createCtx<ViewTemplateState>();
export const [useViewTemplateActions, ViewTemplateActionsProvider] =
  createCtx<ViewTemplateActions>();

/**
 * Whether unsaved filter/sort/group edits for this view template should be persisted to local
 * storage and restored on mount. This is intended only for the primary collection view page a
 * user browses and edits directly - other usages that embed a view template read-only or with
 * their own overriding filters (e.g. ReferencedInEditField) should leave this false so they
 * always reflect only the filters explicitly passed in, never a stale edit from elsewhere.
 *
 * Kept out of ViewTemplateState on purpose: it flips whenever a card modal opens or closes over
 * the collection view, and as part of the shared state value that flip re-rendered every
 * useViewTemplateState consumer — including each group's card service. Only the two Preserve*
 * bridges and ViewLayoutProvider actually read it.
 */
const PreserveEditsContext = createContext(false);
export const PreserveEditsProvider = PreserveEditsContext.Provider;

export function usePreserveEdits(): boolean {
  return useContext(PreserveEditsContext);
}
